/**
 * Offline Database Manager
 * Usa IndexedDB nativo para almacenar datos cuando no hay conexión
 */

const DB_NAME = 'AppOfflineDB';
const DB_VERSION = 1;

class OfflineDB {
    constructor() {
        this.db = null;
    }

    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Store para pedidos pendientes
                if (!db.objectStoreNames.contains('pendingOrders')) {
                    const orderStore = db.createObjectStore('pendingOrders', {
                        keyPath: 'id',
                        autoIncrement: true
                    });
                    orderStore.createIndex('synced', 'synced', { unique: false });
                    orderStore.createIndex('created_at', 'created_at', { unique: false });
                }

                // Store para menú en cache
                if (!db.objectStoreNames.contains('menuCache')) {
                    const menuStore = db.createObjectStore('menuCache', {
                        keyPath: 'item_id'
                    });
                    menuStore.createIndex('category', 'category', { unique: false });
                }

                // Store para categorías en cache
                if (!db.objectStoreNames.contains('categoriesCache')) {
                    db.createObjectStore('categoriesCache', {
                        keyPath: 'category_id'
                    });
                }
            };
        });
    }

    // === PEDIDOS PENDIENTES ===
    /**
     * Guarda un pedido pendiente (Modificado para Multi-Tenant)
     * @param {Object} orderData - Datos del pedido
     * @param {string} companyId - ID de la empresa (OBLIGATORIO)
     */
    async savePendingOrder(orderData, companyId) {
        if (!this.db) await this.init();

        if (!companyId) {
            console.error("❌ Intento de guardar pedido offline sin companyId");
            return;
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['pendingOrders'], 'readwrite');
            const store = transaction.objectStore('pendingOrders');

            const order = {
                data: orderData,
                company_id: companyId, // Tenant Isolation
                created_at: new Date().toISOString(),
                synced: 0,
                retries: 0
            };

            const request = store.add(order);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getPendingOrders() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['pendingOrders'], 'readonly');
            const store = transaction.objectStore('pendingOrders');
            const index = store.index('synced');

            const request = index.getAll(0);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async markOrderAsSynced(id) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['pendingOrders'], 'readwrite');
            const store = transaction.objectStore('pendingOrders');

            const request = store.get(id);

            request.onsuccess = () => {
                const order = request.result;
                if (order) {
                    order.synced = 1;
                    order.synced_at = new Date().toISOString();
                    const updateRequest = store.put(order);
                    updateRequest.onsuccess = () => resolve(true);
                    updateRequest.onerror = () => reject(updateRequest.error);
                } else {
                    resolve(false);
                }
            };

            request.onerror = () => reject(request.error);
        });
    }

    async deleteSyncedOrders() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['pendingOrders'], 'readwrite');
            const store = transaction.objectStore('pendingOrders');
            const index = store.index('synced');

            const request = index.openCursor(1); // synced = 1

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve(true);
                }
            };

            request.onerror = () => reject(request.error);
        });
    }

    // === CACHE DE MENÚ ===
    async cacheMenu(items) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['menuCache'], 'readwrite');
            const store = transaction.objectStore('menuCache');

            // Limpiar cache anterior
            store.clear();

            // Agregar items nuevos
            items.forEach(item => store.add(item));

            transaction.oncomplete = () => resolve(true);
            transaction.onerror = () => reject(transaction.error);
        });
    }

    async getMenuFromCache() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['menuCache'], 'readonly');
            const store = transaction.objectStore('menuCache');
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    // === CACHE DE CATEGORÍAS ===
    async cacheCategories(categories) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['categoriesCache'], 'readwrite');
            const store = transaction.objectStore('categoriesCache');

            store.clear();
            categories.forEach(cat => store.add(cat));

            transaction.oncomplete = () => resolve(true);
            transaction.onerror = () => reject(transaction.error);
        });
    }

    async getCategoriesFromCache() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['categoriesCache'], 'readonly');
            const store = transaction.objectStore('categoriesCache');
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    // === UTILIDADES ===
    async clearAll() {
        if (!this.db) await this.init();

        const stores = ['pendingOrders', 'menuCache', 'categoriesCache'];
        const transaction = this.db.transaction(stores, 'readwrite');

        stores.forEach(storeName => {
            transaction.objectStore(storeName).clear();
        });

        return new Promise((resolve) => {
            transaction.oncomplete = () => resolve(true);
        });
    }

    async getStats() {
        if (!this.db) await this.init();

        const pending = await this.getPendingOrders();
        const menu = await this.getMenuFromCache();
        const categories = await this.getCategoriesFromCache();

        return {
            pendingOrders: pending.length,
            cachedMenuItems: menu.length,
            cachedCategories: categories.length,
            lastUpdate: new Date().toISOString()
        };
    }
}

// Singleton instance
export const offlineDB = new OfflineDB();

// Inicializar en startup
if (typeof window !== 'undefined') {
    offlineDB.init().then(() => {
        console.log('✅ Offline Database inicializada');
    }).catch((error) => {
        console.error('❌ Error inicializando Offline DB:', error);
    });
}
